import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchWeatherContext } from "@/lib/api/agent";
import {
  getDataPointHistory,
  listDataPointSources,
  listRecipes,
  type Building,
  type DataPointNameMeta,
  type DataPointSample,
  type DataPointSource,
  type Recipe,
} from "@/lib/api/building";
import { Chip } from "@/components/control-tower/Chip";
import { Input } from "@/components/ui/input";
import { dataPointLabel, labelsFromSources } from "@/lib/data-point-labels";
import {
  detectGaps,
  staleSinceLabel,
  GAP_FILL,
  GAP_FILL_OPACITY,
} from "@/lib/chart-gaps";
import { useBuildingChangeTick } from "@/lib/building-changes";
import { cardSubtle } from "@/lib/card-styles";
import { formatSignalNumericValue } from "@/lib/format-signal-value";
import { cn } from "@/lib/utils";
import { useEquipmentTypeMeta } from "@/lib/equipment-type-meta";
import {
  MAX_COMPARE_BUILDING_COUNT,
  build_trend_series,
  default_selected_building_ids,
  group_series_into_panels,
  instance_chip_label,
  matching_source_ids,
  type_groups_for_kind,
  type TrendKind,
  type TrendSeries,
  type TypeGroup,
} from "./panel-grouping";

// ── Constants ─────────────────────────────────────────────────────────────────

const SOURCE_PALETTE = [
  "#3b82f6",
  "#f97316",
  "#4ade80",
  "#f43f5e",
  "#a78bfa",
  "#38bdf8",
  "#fbbf24",
  "#e879f9",
  "#84cc16",
  "#14b8a6",
];

const MAX_SIGNAL_NAMES = 8;
const MAX_SOURCES = 6;
// API max (`pagination_data_points_max`): 8 names × 30d hourly is 5760 rows
// from one source, which the default 5000-row history fetch would clip.
const HISTORY_LIMIT = 20_000;

// ── Helpers ───────────────────────────────────────────────────────────────────

function hour_bucket(iso: string): string {
  const d = new Date(iso);
  d.setMinutes(0, 0, 0);
  return d.toISOString();
}

function fmt_ts(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}. ${String(d.getHours()).padStart(2, "0")}:00`;
}

function name_label(name: string, labels: Record<string, string>): string {
  return dataPointLabel(name, labels);
}

function unique_names(series: TrendSeries[]): string[] {
  return [...new Set(series.map((item) => item.name))];
}

function series_legend(
  item: ColoredSeries,
  labels: Record<string, string>,
  multi_building: boolean,
  multi_name: boolean,
): string {
  const signal = name_label(item.name, labels);
  if (multi_building && multi_name) return `${item.building_name} · ${signal}`;
  if (multi_building) return item.building_name;
  if (multi_name) return `${item.source_name} · ${signal}`;
  return item.source_name;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface SourceMeta extends DataPointSource {
  color: string;
  // Tagged once here, at the same load_sources pass that already assigns `color` — the
  // Instanssit chip label (building_name_by_id lookup below) reads it off source_pool_by_id
  // instead of a separate selected_building_ids × sources_by_building traversal.
  building_id: string;
}

interface ColoredSeries extends TrendSeries {
  color: string;
}

interface ScopedSample extends DataPointSample {
  building_id: string;
}

interface ChartRow {
  ts: string;
  is_future: number;
  [key: string]: number | null | string; // keyed by series_key
}

interface TimeGap {
  from_ts: string;
  to_ts: string;
}

interface RecipeAnnotation {
  ts: string;
}

// ── Chart data builder ────────────────────────────────────────────────────────

function build_panels(
  series: ColoredSeries[],
  history: ScopedSample[],
  weather_pts: { ts: string; temp: number }[],
  show_kon: boolean,
): { rows: ChartRow[]; gaps: TimeGap[]; now_bucket: string } {
  const now_b = hour_bucket(new Date().toISOString());
  const row_map = new Map<string, ChartRow>();
  const series_by_match = new Map<string, ColoredSeries>();
  for (const item of series) {
    series_by_match.set(
      `${item.building_id}|${item.source_id}|${item.name}`,
      item,
    );
  }

  function get_row(bucket: string): ChartRow {
    if (!row_map.has(bucket))
      row_map.set(bucket, { ts: bucket, is_future: bucket >= now_b ? 1 : 0 });
    return row_map.get(bucket)!;
  }

  // Keep the latest sample per (hour bucket, series): the plotted value must be
  // deterministic, not "whichever row the API happened to return first".
  const chosen_ts = new Map<string, string>();
  for (const sample of history) {
    if (sample.numeric_value === null) continue;
    const match = series_by_match.get(
      `${sample.building_id}|${sample.source_id}|${sample.name}`,
    );
    if (!match) continue;
    const bucket = hour_bucket(sample.recorded_at);
    const key = `${bucket}|${match.series_key}`;
    const kept = chosen_ts.get(key);
    if (kept !== undefined && sample.recorded_at <= kept) continue;
    chosen_ts.set(key, sample.recorded_at);
    get_row(bucket)[match.series_key] = sample.numeric_value;
  }

  const show_weather =
    show_kon && series.some((item) => item.name === "temperature");
  if (show_weather) {
    for (const { ts, temp } of weather_pts) {
      get_row(ts)["__weather__"] = temp;
    }
  }

  const sorted = [...row_map.values()].sort((a, b) => a.ts.localeCompare(b.ts));

  // Gap detection is shared with the chat trend widgets (chart-gaps.ts): the
  // per-channel threshold, adapted to this series' own cadence, decides when a
  // silent feed is stale — replacing the old flat ">24h" rule.
  const detected = detectGaps(sorted, unique_names(series), (r) => r.ts);
  const gap_span = new Set(detected.map((g) => `${g.from}|${g.to}`));

  const gaps: TimeGap[] = detected.map((g) => ({
    from_ts: g.from,
    to_ts: g.to,
  }));
  const with_fills: ChartRow[] = [];
  for (let i = 0; i < sorted.length; i++) {
    with_fills.push(sorted[i]);
    if (
      i < sorted.length - 1 &&
      gap_span.has(`${sorted[i].ts}|${sorted[i + 1].ts}`)
    ) {
      // Sparse null-valued rows inside the gap so the line (connectNulls off)
      // breaks across the outage instead of drawing a confident straight line.
      const a_ms = new Date(sorted[i].ts).getTime();
      const b_ms = new Date(sorted[i + 1].ts).getTime();
      for (let k = 1; k <= 6; k++) {
        const t = new Date(a_ms + ((b_ms - a_ms) * k) / 7);
        with_fills.push({
          ts: t.toISOString(),
          is_future: t.toISOString() >= now_b ? 1 : 0,
        });
      }
    }
  }

  const rows =
    with_fills.length <= 400
      ? with_fills
      : with_fills.filter(
          (_, i) => i % Math.ceil(with_fills.length / 400) === 0,
        );

  return { rows, gaps, now_bucket: now_b };
}

// The overlay is per-instance and scope-adaptive: the capped `selected_ids` set already carries every matching instance across
// every selected building, so per-building history fetch is just "this building's slice of it" —
// never a re-derived representative.
function history_source_ids(
  sources: SourceMeta[],
  selected_ids: string[],
): string[] {
  const this_building = new Set(sources.map((s) => s.source_id));
  return selected_ids.filter((id) => this_building.has(id));
}

// ── TrendView ─────────────────────────────────────────────────────────────────

interface Props {
  /** Single-building scope (ADR-014) — set when the ScopeBar has one building selected. */
  buildingId: string | null;
  /**
   * Candidate buildings for Kiinteistösalkku/-kanta scope, resolved by the route
   * (useFilteredBuildings) from the ScopeBar's active group / attribute filters. Ignored once
   * `buildingId` is set. The picker selects a bounded subset of this pool; history is fetched
   * per selected building via getDataPointHistory.
   */
  candidateBuildings?: Building[];
}

export function TrendView({ buildingId, candidateBuildings = [] }: Props) {
  const equipment_type_meta = useEquipmentTypeMeta();

  const [days_back, set_days_back] = useState(7);
  const [selected_building_ids, set_selected_building_ids] = useState<string[]>(
    () => default_selected_building_ids(buildingId, candidateBuildings),
  );
  const [selected_kind, set_selected_kind] =
    useState<TrendKind>("talotekniikka");
  const [selected_type_keys, set_selected_type_keys] = useState<string[]>([]);
  const [selected_ids, set_selected_ids] = useState<string[]>([]);
  const [selected_names, set_selected_names] = useState<string[]>([]);
  const [split_units, set_split_units] = useState<string[]>([]);
  const [show_kon, set_show_kon] = useState(true);
  const [show_suo, set_show_suo] = useState(true);

  const [sources_by_building, set_sources_by_building] = useState<
    Record<string, SourceMeta[]>
  >({});
  const [dp_labels, set_dp_labels] = useState<Record<string, string>>({});
  const [history, set_history] = useState<ScopedSample[]>([]);
  const [weather_pts, set_weather_pts] = useState<
    { ts: string; temp: number }[]
  >([]);
  const [annotations, set_annotations] = useState<RecipeAnnotation[]>([]);
  const [loading_sources, set_loading_sources] = useState(true);
  const [loading_history, set_loading_history] = useState(false);

  const [nl_query, set_nl_query] = useState("");

  const candidate_ids_key = candidateBuildings.map((b) => b.id).join(",");
  useEffect(() => {
    set_selected_building_ids(
      default_selected_building_ids(buildingId, candidateBuildings),
    );
    // Kind/type are reset only on a real ScopeBar switch (this effect) — toggling a building
    // in/out of a salkku/kanta selection (below) keeps the current kind/type and just
    // recomputes which instances match it.
    set_selected_kind("talotekniikka");
    set_selected_type_keys([]);
    set_selected_ids([]);
    set_selected_names([]);
    set_split_units([]);
  }, [buildingId, candidate_ids_key]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected_buildings_key = selected_building_ids.join(",");
  const is_multi = selected_building_ids.length > 1;
  // New readings in the building in scope re-read the plotted history (paced by the stream).
  const telemetry_tick = useBuildingChangeTick(buildingId, "telemetry");

  useEffect(() => {
    if (selected_building_ids.length === 0) {
      set_sources_by_building({});
      set_dp_labels({});
      set_weather_pts([]);
      set_annotations([]);
      set_loading_sources(false);
      return;
    }
    void load_sources(selected_building_ids);
  }, [selected_buildings_key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const can_fetch =
      selected_building_ids.length > 0 &&
      selected_names.length > 0 &&
      selected_ids.length > 0;
    if (can_fetch) {
      void load_history(selected_building_ids);
    } else {
      set_history([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot key, not the fetch closure
  }, [
    selected_buildings_key,
    selected_ids,
    selected_names,
    days_back,
    sources_by_building,
    telemetry_tick,
  ]);

  async function load_sources(building_ids: string[]) {
    set_loading_sources(true);
    const single = building_ids.length === 1;
    const results = await Promise.all(
      building_ids.map(async (id) => {
        const [raw_sources, recipes, weather] = await Promise.all([
          listDataPointSources(id).catch((): DataPointSource[] => []),
          single
            ? listRecipes(id).catch((): Recipe[] => [])
            : Promise.resolve([] as Recipe[]),
          single
            ? fetchWeatherContext(id).catch(() => null)
            : Promise.resolve(null),
        ]);
        return { id, raw_sources, recipes, weather };
      }),
    );

    const next_sources: Record<string, SourceMeta[]> = {};
    const all_raw: DataPointSource[] = [];
    let color_i = 0;
    for (const { id, raw_sources } of results) {
      all_raw.push(...raw_sources);
      next_sources[id] = raw_sources.map((s) => ({
        ...s,
        color: SOURCE_PALETTE[color_i++ % SOURCE_PALETTE.length],
        building_id: id,
      }));
    }

    const primary = results[0];
    const now_iso = new Date().toISOString();
    const wp = (primary?.weather?.points ?? [])
      .filter((p) => p.hour >= now_iso)
      .map((p) => ({ ts: hour_bucket(p.hour), temp: p.temperature_c }));

    const ann: RecipeAnnotation[] = [];
    for (const r of primary?.recipes ?? []) {
      if (r.status !== "scheduled" && r.status !== "applied") continue;
      for (const a of r.actions) {
        if (!a.scheduled_at) continue;
        const b = hour_bucket(a.scheduled_at);
        if (!ann.find((x) => x.ts === b)) ann.push({ ts: b });
      }
    }

    set_sources_by_building(next_sources);
    set_dp_labels(labelsFromSources(all_raw));
    set_weather_pts(wp);
    set_annotations(ann);
    set_loading_sources(false);
    // No default kind/type/instance selection is picked here — the four-step contract
    // is user-driven; the matched_instance_ids effect below only
    // resyncs the *default* instance set once a type is actually chosen.
  }

  async function load_history(building_ids: string[]) {
    set_loading_history(true);
    const from_ts = new Date(
      Date.now() - days_back * 24 * 3_600_000,
    ).toISOString();
    const chunks = await Promise.all(
      building_ids.map(async (id) => {
        const sources = sources_by_building[id] ?? [];
        const source_ids = history_source_ids(sources, selected_ids);
        if (source_ids.length === 0 || selected_names.length === 0) return [];
        const data = await getDataPointHistory(
          id,
          source_ids,
          selected_names,
          from_ts,
          HISTORY_LIMIT,
        ).catch((): DataPointSample[] => []);
        return data.map((sample): ScopedSample => ({
          ...sample,
          building_id: id,
        }));
      }),
    );
    set_history(chunks.flat());
    set_loading_history(false);
  }

  // The pool the Type step groups over — one building's sources at building scope, every
  // selected building's sources at Kiinteistösalkku/-kanta. A single
  // building is just the flatMap of one id, so there is no separate single-building memo.
  const all_selected_sources = useMemo((): SourceMeta[] => {
    return selected_building_ids.flatMap((id) => sources_by_building[id] ?? []);
  }, [selected_building_ids, sources_by_building]);
  const source_pool = all_selected_sources;

  const type_groups = useMemo(
    (): TypeGroup[] =>
      type_groups_for_kind(source_pool, selected_kind, equipment_type_meta),
    [source_pool, selected_kind, equipment_type_meta],
  );

  // Built once per source_pool change so the step-2b instance-chip row (below) does a map
  // lookup per chip instead of an O(source_pool.length) .find() per chip.
  const source_pool_by_id = useMemo(
    () => new Map(source_pool.map((src) => [src.source_id, src])),
    [source_pool],
  );

  // Every instance across the selected Type pills, deterministically ordered — the caller
  // (below) applies the MAX_SOURCES bounded-overlay cap when a type is (de)selected.
  const matched_instance_ids = useMemo(
    () => matching_source_ids(type_groups, selected_type_keys),
    [type_groups, selected_type_keys],
  );

  // Reconciles selected_ids whenever the matching pool changes — an explicit Kind/Type click
  // (select_kind/toggle_type below) as well as an incidental pool change (a building added to/
  // removed from a salkku/kanta scope, or a fresh sources_by_building load). Ids no longer
  // matching are dropped; if that leaves room under MAX_SOURCES, newly matching ids are appended
  // (deterministic group order) up to the cap. This is the one mechanism for both cases, so a
  // step-2b manual refinement (toggle_instance) survives a scope broaden/narrow instead of being
  // silently reset to the front-capped default every time.
  useEffect(() => {
    set_selected_ids((prev) => {
      const matched = new Set(matched_instance_ids);
      const kept = prev.filter((id) => matched.has(id));
      if (kept.length >= MAX_SOURCES) return kept.slice(0, MAX_SOURCES);
      const kept_set = new Set(kept);
      const additions = matched_instance_ids.filter((id) => !kept_set.has(id));
      return [...kept, ...additions].slice(0, MAX_SOURCES);
    });
  }, [matched_instance_ids]);

  const selected_ids_set = useMemo(() => new Set(selected_ids), [selected_ids]);

  const selected_sources = useMemo(
    () => source_pool.filter((src) => selected_ids_set.has(src.source_id)),
    [source_pool, selected_ids_set],
  );

  const available_names = useMemo((): DataPointNameMeta[] => {
    if (selected_sources.length === 0) return [];
    const seen = new Map<string, DataPointNameMeta>();
    for (const src of selected_sources) {
      for (const nm of src.available_names) {
        if (!seen.has(nm.name)) seen.set(nm.name, nm);
      }
    }
    return [...seen.values()];
  }, [selected_sources]);

  // A signal chosen for a since-removed instance/type is no longer plottable — drop it instead
  // of leaving a selected-looking chip that renders no series.
  useEffect(() => {
    set_selected_names((prev) =>
      prev.filter((n) => available_names.some((nm) => nm.name === n)),
    );
  }, [available_names]);

  const buildings_for_series = useMemo(() => {
    if (buildingId) {
      const named = candidateBuildings.find((b) => b.id === buildingId);
      return [{ id: buildingId, name: named?.name ?? buildingId }];
    }
    return candidateBuildings;
  }, [buildingId, candidateBuildings]);

  // Building name lookup for the Instanssit chip label (instance_chip_label below) — scoped to
  // buildings_for_series (bounded by MAX_COMPARE_BUILDING_COUNT), not a pass over every source,
  // since each SourceMeta already carries its own building_id (tagged in load_sources) and
  // source_pool_by_id already indexes every source by id.
  const building_name_by_id = useMemo(
    () => new Map(buildings_for_series.map((b) => [b.id, b.name])),
    [buildings_for_series],
  );

  const trend_series = useMemo((): ColoredSeries[] => {
    const raw = build_trend_series({
      selected_building_ids,
      buildings: buildings_for_series,
      sources_by_building,
      selected_source_ids: selected_ids,
      selected_names,
    });
    return raw.map((item, i) => ({
      ...item,
      color: SOURCE_PALETTE[i % SOURCE_PALETTE.length],
    }));
  }, [
    selected_building_ids,
    buildings_for_series,
    sources_by_building,
    selected_ids,
    selected_names,
  ]);

  const split_set = useMemo(() => new Set(split_units), [split_units]);
  const panels = useMemo(
    () => group_series_into_panels(trend_series, split_set),
    [trend_series, split_set],
  );

  function toggle_building(id: string) {
    set_selected_building_ids((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id].slice(-MAX_COMPARE_BUILDING_COUNT);
    });
  }

  // Kind step — Talotekniikka | Vyöhykkeet are peer kinds; switching kind starts the Type/
  // instance selection over, since the two pools never mix. Clearing
  // selected_type_keys drops matched_instance_ids to [], and the reconciliation effect above
  // then clears selected_ids too — one mechanism, not a second reset here.
  function select_kind(kind: TrendKind) {
    set_selected_kind(kind);
    set_selected_type_keys([]);
  }

  // Type step — toggling a type (de)selects it; the reconciliation effect above keeps any
  // still-matching instances (a manual step-2b refinement of an already-selected type survives)
  // and tops up with the newly (un)matched type's instances, deterministically ordered and
  // capped at MAX_SOURCES. Multi-select so Vyöhykkeet
  // (where each zone is its own type group) can still overlay several zones at once.
  function toggle_type(type_key: string) {
    set_selected_type_keys((prev) =>
      prev.includes(type_key)
        ? prev.filter((k) => k !== type_key)
        : [...prev, type_key],
    );
  }

  // Step 2b (optional) — refine which instances of the already-chosen type(s) are plotted.
  // Never required: selecting a type alone already yields a capped overlay. Caps by dropping the
  // oldest addition (slice(-MAX_SOURCES)) rather than the deterministic front-cap the
  // reconciliation effect uses for defaults — same drop-oldest convention as toggle_building
  // above, appropriate for a manual multi-select row the user is actively clicking through.
  function toggle_instance(id: string) {
    set_selected_ids((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id].slice(-MAX_SOURCES),
    );
  }

  function toggle_name(name: string) {
    set_selected_names((prev) =>
      prev.includes(name)
        ? prev.filter((x) => x !== name)
        : [...prev, name].slice(-MAX_SIGNAL_NAMES),
    );
  }

  function toggle_split(unit: string) {
    const trimmed = unit.trim();
    if (!trimmed) return;
    set_split_units((prev) =>
      prev.includes(trimmed)
        ? prev.filter((u) => u !== trimmed)
        : [...prev, trimmed],
    );
  }

  const building_picker = !buildingId && candidateBuildings.length > 0 && (
    <div className="flex flex-wrap items-center gap-2">
      <span className="shrink-0 text-xs text-muted-foreground">
        Kiinteistö{" "}
        <span className="text-muted-foreground/50">
          ({selected_building_ids.length}/{candidateBuildings.length}, max{" "}
          {MAX_COMPARE_BUILDING_COUNT})
        </span>
      </span>
      {candidateBuildings.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => toggle_building(b.id)}
          className={cn(
            "rounded-full border px-3 py-1 text-xs transition-colors",
            selected_building_ids.includes(b.id)
              ? "border-primary/40 bg-primary/10 text-primary font-medium"
              : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
          )}
        >
          {b.name}
        </button>
      ))}
    </div>
  );

  if (selected_building_ids.length === 0) {
    return (
      <div className="space-y-4">
        {building_picker}
        <div
          className={cn(
            cardSubtle,
            "flex items-center justify-center border-dashed py-16 text-sm text-muted-foreground",
          )}
        >
          Ei kiinteistöjä valitussa laajuudessa.
        </div>
      </div>
    );
  }

  if (loading_sources) {
    return (
      <div className="space-y-4">
        {building_picker}
        <p className="py-16 text-center text-sm text-muted-foreground">
          Ladataan trendidataa…
        </p>
      </div>
    );
  }

  if (all_selected_sources.length === 0) {
    return (
      <div className="space-y-4">
        {building_picker}
        <div
          className={cn(
            cardSubtle,
            "flex items-center justify-center border-dashed py-16 text-sm text-muted-foreground",
          )}
        >
          Ei signaaleja. Aja seed-skripti lisätäksesi esimerkkidataa.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {building_picker}
      {/* Time scope — window chips only; zoom/pan is out of scope (#1191). */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Aikajakso:</span>
        {([1, 7, 30] as const).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => set_days_back(d)}
            className={cn(
              "rounded-full border px-3 py-0.5 text-xs transition-colors",
              days_back === d
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
            )}
          >
            {d === 1 ? "1 vrk" : d === 7 ? "7 vrk" : "30 vrk"}
          </button>
        ))}
      </div>

      {/* Kind step — Talotekniikka | Vyöhykkeet as peer kinds; zones never mix into the
          Talotekniikka row and vice versa. Works the same way at building scope and at Kiinteistösalkku/-kanta scope. */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Laji:</span>
        <Chip
          active={selected_kind === "talotekniikka"}
          label="Talotekniikka"
          onClick={() => select_kind("talotekniikka")}
        />
        <Chip
          active={selected_kind === "vyohykkeet"}
          label="Vyöhykkeet"
          onClick={() => select_kind("vyohykkeet")}
        />
      </div>

      {/* Type step — types present for the chosen kind. Choosing one auto-selects every matching
          instance up to MAX_SOURCES, per-instance and scope-adaptive — no per-instance
          click-through required to see a series. */}
      {type_groups.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs text-muted-foreground">Tyyppi</p>
          <div className="flex flex-wrap gap-2">
            {type_groups.map((group) => (
              <Chip
                key={group.key}
                active={selected_type_keys.includes(group.key)}
                label={group.label}
                onClick={() => toggle_type(group.key)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Step 2b (optional) — refine which instances of the chosen type(s) are plotted; the
          default (every matching instance, capped) already plots without touching this row. */}
      {matched_instance_ids.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            Instanssit{" "}
            <span className="text-muted-foreground/50">
              ({selected_ids.length} / {matched_instance_ids.length}, max{" "}
              {MAX_SOURCES})
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {matched_instance_ids.map((id) => {
              const instance = source_pool_by_id.get(id);
              if (!instance) return null;
              return (
                <Chip
                  key={id}
                  active={selected_ids_set.has(id)}
                  label={instance_chip_label(
                    instance.source_name,
                    building_name_by_id.get(instance.building_id),
                    is_multi,
                  )}
                  onClick={() => toggle_instance(id)}
                />
              );
            })}
          </div>
        </div>
      )}

      {available_names.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs text-muted-foreground">
            Signaali{" "}
            <span className="text-muted-foreground/50">
              (max {MAX_SIGNAL_NAMES})
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {available_names.map((nm) => {
              const is_sel = selected_names.includes(nm.name);
              return (
                <button
                  key={nm.name}
                  type="button"
                  onClick={() => toggle_name(nm.name)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-colors",
                    is_sel
                      ? "border-primary/40 bg-primary/10 text-primary font-medium"
                      : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
                  )}
                >
                  {name_label(nm.name, dp_labels)}
                  {nm.unit && (
                    <span className="ml-1 text-muted-foreground/60">
                      {nm.unit}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!is_multi && (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-muted-foreground">Kerrokset:</span>
          <LayerToggle
            label="Sää"
            color="#4db6ac"
            active={show_kon}
            onChange={set_show_kon}
          />
          <LayerToggle
            label="Reseptit"
            color="#4caf82"
            active={show_suo}
            onChange={set_show_suo}
          />
        </div>
      )}

      {selected_names.length === 0 ? (
        <div
          className={cn(
            cardSubtle,
            "flex items-center justify-center border-dashed py-12 text-sm text-muted-foreground",
          )}
        >
          Valitse signaali yllä nähdäksesi trendin.
        </div>
      ) : loading_history ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Ladataan…
        </p>
      ) : (
        <div className="space-y-3">
          {panels.map((panel) => {
            const { rows, gaps, now_bucket } = build_panels(
              panel.series,
              history,
              weather_pts,
              show_kon && !is_multi,
            );
            const names = unique_names(panel.series);
            const unit_key = panel.unit.trim();
            const split_active = split_set.has(unit_key);
            const can_split =
              unit_key !== "" && (names.length > 1 || split_active);
            return (
              <ChannelPanel
                key={panel.panel_key}
                title={
                  names.length === 1
                    ? name_label(names[0], dp_labels)
                    : panel.unit || "Signaali"
                }
                unit={panel.unit}
                rows={rows}
                gaps={gaps}
                now_bucket={now_bucket}
                series={panel.series}
                annotations={show_suo && !is_multi ? annotations : []}
                show_weather={
                  show_kon &&
                  !is_multi &&
                  panel.series.some((s) => s.name === "temperature") &&
                  weather_pts.length > 0
                }
                labels={dp_labels}
                multi_building={is_multi}
                can_split={can_split}
                split_active={split_active}
                onToggleSplit={
                  can_split ? () => toggle_split(panel.unit) : undefined
                }
              />
            );
          })}
        </div>
      )}

      <div className="border-t border-border/40 pt-4">
        <p className="mb-2 text-xs text-muted-foreground">
          Hae signaaleja nimellä
        </p>
        <Input
          placeholder='Esim. "CO₂" tai "lämpötila"'
          value={nl_query}
          onChange={(e) => set_nl_query(e.target.value)}
          className="max-w-sm"
        />
        {nl_query.trim() && (
          <div className="mt-2 flex flex-wrap gap-2">
            {available_names
              .filter(
                (nm) =>
                  name_label(nm.name, dp_labels)
                    .toLowerCase()
                    .includes(nl_query.toLowerCase()) ||
                  nm.name.toLowerCase().includes(nl_query.toLowerCase()),
              )
              .map((nm) => (
                <button
                  key={nm.name}
                  type="button"
                  onClick={() => {
                    toggle_name(nm.name);
                    set_nl_query("");
                  }}
                  className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs text-primary"
                >
                  {name_label(nm.name, dp_labels)}
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ChannelPanel ──────────────────────────────────────────────────────────────

function ChannelPanel({
  title,
  unit,
  rows,
  gaps,
  now_bucket,
  series,
  annotations,
  show_weather,
  labels,
  multi_building,
  can_split,
  split_active,
  onToggleSplit,
}: {
  title: string;
  unit: string;
  rows: ChartRow[];
  gaps: TimeGap[];
  now_bucket: string;
  series: ColoredSeries[];
  annotations: RecipeAnnotation[];
  show_weather: boolean;
  labels: Record<string, string>;
  multi_building: boolean;
  can_split: boolean;
  split_active: boolean;
  onToggleSplit?: () => void;
}) {
  const multi_name = unique_names(series).length > 1;

  return (
    <div className={cn(cardSubtle, "border-border/50 bg-card/30 p-3")}>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-medium text-foreground">{title}</span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
        {can_split && onToggleSplit && (
          <button
            type="button"
            onClick={onToggleSplit}
            className={cn(
              "rounded-full border px-2 py-0.5 text-[10px] transition-colors",
              split_active
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
            )}
          >
            {split_active ? "Yhdistä" : "Jaa"}
          </button>
        )}
        <div className="ml-auto flex flex-wrap justify-end gap-3">
          {series.map((item) => {
            const last = [...rows]
              .reverse()
              .find((r) => r[item.series_key] != null);
            const val = last ? (last[item.series_key] as number) : null;
            return (
              <span
                key={item.series_key}
                className="flex items-center gap-1 text-xs"
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-muted-foreground/70">
                  {series_legend(item, labels, multi_building, multi_name)}
                </span>
                {val != null && (
                  <span className="font-medium" style={{ color: item.color }}>
                    {formatSignalNumericValue(val, unit)}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-xs text-muted-foreground/60">
          Ei dataa valitulle ajanjaksolle
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart
            data={rows}
            margin={{
              top: 4,
              right: show_weather ? 50 : 16,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="ts"
              tickFormatter={fmt_ts}
              tick={{ fontSize: 9 }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="main"
              orientation="left"
              tick={{ fontSize: 9 }}
              tickLine={false}
              axisLine={false}
              unit={unit ? ` ${unit}` : ""}
              width={46}
            />
            {show_weather && (
              <YAxis
                yAxisId="weather"
                orientation="right"
                tick={{ fontSize: 9 }}
                tickLine={false}
                axisLine={false}
                unit=" °C"
                width={38}
              />
            )}
            <Tooltip
              labelFormatter={fmt_ts}
              formatter={(value: number, key: string) => {
                if (key === "__weather__")
                  return [`${value.toFixed(1)} °C`, "Ulkolämpötila"];
                const item = series.find((s) => s.series_key === key);
                const label = item
                  ? series_legend(item, labels, multi_building, multi_name)
                  : key;
                return [formatSignalNumericValue(value, unit), label];
              }}
            />

            {gaps.map((gap, i) => (
              <ReferenceArea
                key={`gap-${i}`}
                x1={gap.from_ts}
                x2={gap.to_ts}
                yAxisId="main"
                fill={GAP_FILL}
                fillOpacity={GAP_FILL_OPACITY}
                stroke="none"
                label={{
                  value: staleSinceLabel(gap.from_ts, fmt_ts),
                  position: "insideTop",
                  fill: "var(--warning)",
                  fontSize: 9,
                }}
              />
            ))}

            <ReferenceArea
              x1={now_bucket}
              yAxisId="main"
              fill="rgba(255,255,255,0.03)"
              fillOpacity={1}
            />
            <ReferenceLine
              x={now_bucket}
              yAxisId="main"
              stroke="var(--muted-foreground)"
              strokeDasharray="4 2"
              strokeOpacity={0.4}
              label={{
                value: "nyt",
                fill: "var(--muted-foreground)",
                fontSize: 9,
                position: "insideTopLeft",
              }}
            />

            {annotations.map((a, i) => (
              <ReferenceLine
                key={i}
                x={a.ts}
                yAxisId="main"
                stroke="#4caf82"
                strokeDasharray="3 2"
                strokeOpacity={0.6}
              />
            ))}

            {series.map((item) => (
              <Line
                key={item.series_key}
                dataKey={item.series_key}
                yAxisId="main"
                stroke={item.color}
                dot={false}
                strokeWidth={1.5}
                name={item.series_key}
              />
            ))}

            {show_weather && (
              <Line
                dataKey="__weather__"
                yAxisId="weather"
                stroke="#4db6ac"
                strokeDasharray="5 3"
                dot={false}
                strokeWidth={1.5}
                name="__weather__"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── LayerToggle ───────────────────────────────────────────────────────────────

function LayerToggle({
  label,
  color,
  active,
  onChange,
}: {
  label: string;
  color: string;
  active: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!active)}
      className={cn(
        "flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors",
        active
          ? "border-transparent text-white"
          : "border-border text-muted-foreground",
      )}
      style={active ? { backgroundColor: color } : undefined}
    >
      <span
        className="inline-block h-1.5 w-3 rounded-full opacity-80"
        style={{ backgroundColor: active ? "white" : color }}
      />
      {label}
    </button>
  );
}
